/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { withRouter } from 'react-router-dom';

import {
    acceptCustomerRecurringBooking,
    getCustomerRecurringBookingDates
} from 'school-space-client/api';
import { Button, Heading } from 'school-space-client/components';
import DatesSelector from './DatesSelector';
import { errorHandler } from '../../../../../services';

import sharedCss from './css';

const css = {
    summaryText: theme => ({
        width: '100%',
        marginBottom: theme.layout.baseline,
        textAlign: 'right',
        [theme.helpers.mq('s')]: {
            width: 'auto',
            marginBottom: 0,
            marginRight: theme.layout.baseline
        }
    }),
    errorText: theme => ({
        width: '100%',
        marginBottom: theme.layout.baseline,
        textAlign: 'right',
        color: theme.colors.error,
        [theme.helpers.mq('s')]: {
            width: 'auto',
            marginBottom: 0,
            marginRight: theme.layout.baseline
        }
    })
};

const ReviewIndividualDates = ({ recurringBooking, stepIsConfirmed, stepIsVisible, history }) => {
    const ref = useRef(null);
    const [dates, setDates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const { id, customerId } = recurringBooking;

    useEffect(() => {
        if (stepIsVisible) {
            ref.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }, [stepIsVisible]);

    useEffect(() => {
        setLoading(true);
        getCustomerRecurringBookingDates(customerId, id)
            .then(data => {
                const datesWithSelected = data.map(date => ({
                    ...date,
                    selected: !date.isNonOperational
                }));
                setDates(datesWithSelected);
                setLoading(false);
            })
            .catch(err => console.error(err));
    }, [customerId, id]);

    const toggleDate = useCallback(
        dateToToggle => {
            const newDates = [...dates];
            const index = newDates.findIndex(({ date }) => date === dateToToggle);

            newDates[index].selected = !newDates[index].selected;
            setDates(newDates);
        },
        [dates]
    );

    const deselectHolidays = useCallback(
        () =>
            setDates(
                dates.map(date => ({
                    ...date,
                    selected: date.isSchoolHoliday ? false : date.selected
                }))
            ),
        [dates]
    );

    const selectAll = useCallback(
        () =>
            setDates(
                dates.map(date => ({
                    ...date,
                    selected: !date.isNonOperational
                }))
            ),
        [dates]
    );

    const confirmStep = async () => {
        const datesToSkip = dates
            .filter(({ selected, isNonOperational }) => !isNonOperational && !selected)
            .map(({ date }) => date);

        setSubmitting(true);

        return acceptCustomerRecurringBooking(customerId, id, datesToSkip)
            .then(() => history.push('/repeat-bookings'))
            .catch(error => {
                setSubmitting(false);
                setSubmitError(
                    'There was an error confirming your booking, please refresh and try again, then contact us if the issue persists.'
                );
                errorHandler.report(error);
            });
    };

    const numNonOperationalDates = dates.filter(({ isNonOperational }) => isNonOperational).length;
    const numSkipped =
        dates.filter(({ selected }) => selected === false).length - numNonOperationalDates;
    const numDates = dates.length - numNonOperationalDates;

    return (
        <div ref={ref} css={sharedCss.container}>
            {!stepIsConfirmed && (
                <Heading level="2" size="medium" styles={sharedCss.heading}>
                    3. Skip individual dates
                </Heading>
            )}
            {stepIsVisible && (
                <div css={sharedCss.content}>
                    <DatesSelector
                        dates={dates}
                        toggleDate={toggleDate}
                        deselectHolidays={deselectHolidays}
                        selectAll={selectAll}
                        loading={loading}
                    />
                    <div css={sharedCss.actionButtons}>
                        {!submitError && numSkipped > 0 && (
                            <span
                                css={css.summaryText}
                            >{`You are skipping ${numSkipped} out of ${numDates} dates`}</span>
                        )}
                        {submitError && <span css={css.errorText}>{submitError}</span>}
                        {!stepIsConfirmed && (
                            <Button
                                styles={sharedCss.actionButton}
                                size="small"
                                onClick={confirmStep}
                                disabled={submitting}
                            >
                                Confirm dates
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

ReviewIndividualDates.propTypes = {
    recurringBooking: PropTypes.objectOf(PropTypes.any).isRequired,
    stepIsConfirmed: PropTypes.bool.isRequired,
    stepIsVisible: PropTypes.bool.isRequired,
    history: ReactRouterPropTypes.history.isRequired
};

export default withRouter(ReviewIndividualDates);
